import type { AssetRef, ToolCore } from "@loumy/contracts";
import { useRef, useState } from "react";
import { ApiError } from "../api/client";
import { titleCase } from "../lib/format";
import type { WebToolUI } from "../registry/toolRegistry";
import { FallbackRenderer } from "../results/FallbackRenderer";
import type { JobSignals } from "../results/ResultMetaContext";
import { JobStatusProvider, ResultMetaProvider } from "../results/ResultMetaContext";
import { ResultUrlProvider } from "../results/ResultUrlContext";
import { useUploadAsset } from "../state/queries";
import { useUiStore, type ToolInput } from "../state/uiStore";
import { Icon, Spinner, cx } from "../ui";

// §11 Media-Region: LINKS role-basierte Upload-Dropzones aus core.inputs (D61: server-proxy
// Upload + Inline-Validierung), RECHTS DANEBEN das Ergebnis/Player über die ToolUI.ResultRenderer
// (oder kind-Fallback, D32/D43). Hochgeladene Inputs landen im uiStore (je Tool/Rolle) und
// werden vom SettingsPanel beim Submit als job_inputs mitgeschickt. Die Eingabe-Spalte erscheint,
// sobald ein Input-Slot Eingaben aufnehmen kann (max>0) — inkl. optionaler Refs (image-generator,
// D76); nur Tools ganz ohne Input-Slot (max=0) → keine Spalte, Output über die volle Breite.

// Input-Uploads sind untargeted (Inbox, folder_id=null, §9.4): transiente Eingaben, deren
// Liveness der laufende Job hält (recompute_liveness, §9.11).
const UPLOAD_FOLDER: string | null = null;
// Server-Allowlist (D61/D132) JE KIND — der accept-Filter folgt jetzt den Slot-Kinds
// (`InputRequirement.kinds ?? [kind]`, D132) statt einer globalen Bild-Konstante.
const ACCEPT_BY_KIND: Record<string, string> = {
  image: "image/png,image/jpeg,image/webp",
  video: "video/mp4,video/quicktime,video/webm",
};
const HINT_BY_KIND: Record<string, string> = {
  image: "PNG · JPEG · WebP",
  video: "MP4 · MOV · WebM",
};
// Stabile Leer-Referenz: ein `?? []`-Literal im Zustand-Selector liefert bei jedem Aufruf
// ein neues Array ⇒ instabiler useSyncExternalStore-Snapshot ⇒ Render-Endlosschleife.
const NO_INPUTS: ToolInput[] = [];

function Dropzone({
  toolId,
  role,
  kind,
  kinds,
  min,
  max,
}: {
  toolId: string;
  role: string;
  kind: string;
  /** D132: akzeptierte Kind-Union des Slots (`InputRequirement.kinds ?? [kind]`). */
  kinds: string[];
  min: number;
  max: number;
}) {
  const items = useUiStore((s) => s.inputs[toolId]?.[role] ?? NO_INPUTS);
  const addInput = useUiStore((s) => s.addInput);
  const removeInput = useUiStore((s) => s.removeInput);
  const upload = useUploadAsset();

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const full = items.length >= max;
  // D132: accept-/Hinweis-Strings aus der Kind-Union des Slots (unbekannte Kinds fallen weg).
  const accept = kinds.map((k) => ACCEPT_BY_KIND[k]).filter(Boolean).join(",");
  const hint = kinds.map((k) => HINT_BY_KIND[k]).filter(Boolean).join(" · ");
  const dropLabel = kinds.includes("video")
    ? kinds.includes("image")
      ? "Bild oder Video ablegen oder klicken"
      : "Video ablegen oder klicken"
    : "Bild ablegen oder klicken";

  async function handleFiles(files: FileList | File[]) {
    setError(null);
    const list = Array.from(files);
    // `remaining` aus dem FRISCHEN Store-Stand (nicht dem evtl. veralteten Render-`items`), damit
    // zwei schnell aufeinanderfolgende Drop-/Pick-Aufrufe nicht je `max` Uploads anstoßen. Das harte
    // Gate sitzt zusätzlich zentral in `addInput(…, max)` (Codex-P1) — hier nur, um unnötige Uploads
    // gar nicht erst zu starten.
    const remaining = max - (useUiStore.getState().inputs[toolId]?.[role]?.length ?? 0);
    if (remaining <= 0) return;
    for (const file of list.slice(0, remaining)) {
      const previewUrl = URL.createObjectURL(file);
      // Identitäts-Epoch beim Upload-Start erfassen (D65/Codex-P1): wechselt sie bis zur Completion
      // (Logout/Identitätswechsel → resetUserScopedState), wird das Ergebnis VERWORFEN statt in den
      // bereits geleerten/fremden Store geschrieben (kein Cross-User-Bleed via inflight-Upload).
      const epoch = useUiStore.getState().sessionEpoch;
      try {
        const asset = await upload.mutateAsync({ file, folderId: UPLOAD_FOLDER });
        if (useUiStore.getState().sessionEpoch !== epoch) {
          URL.revokeObjectURL(previewUrl);
          return;
        }
        // `max` durchreichen: `addInput` lehnt bei vollem Slot hart ab (revokt die blob-Preview).
        addInput(toolId, role, { asset, previewUrl }, max);
      } catch (e) {
        URL.revokeObjectURL(previewUrl);
        setError(e instanceof ApiError ? e.message : "Upload fehlgeschlagen");
      }
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center text-[11px] text-muted">
        <span className="font-medium text-text">{titleCase(role)}</span>
      </div>

      {/* Hochgeladene Inputs */}
      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {items.map((it) => (
            <div key={it.asset.id} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-surface-2">
              {it.previewUrl ? (
                // D132: Video-Inputs als stumme Loop-Vorschau (blob-/presigned-URL), Bilder wie gehabt.
                it.asset.kind === "video" ? (
                  <video src={it.previewUrl} className="h-full w-full object-cover" autoPlay loop muted playsInline />
                ) : (
                  <img src={it.previewUrl} alt={role} className="h-full w-full object-cover" />
                )
              ) : (
                <div className="grid h-full place-items-center text-muted">
                  <Icon name="upload" size={20} />
                </div>
              )}
              <button
                type="button"
                aria-label="Entfernen"
                onClick={() => removeInput(toolId, role, it.asset.id)}
                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100"
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone (nur solange Platz ist) */}
      {!full && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
          }}
          className={cx(
            "flex aspect-square max-w-[200px] flex-col items-center justify-center gap-2 rounded-xl2 border border-dashed p-6 text-center transition",
            dragging ? "border-accent bg-accent/10" : "border-border bg-surface-2/60 hover:border-accent/60",
          )}
        >
          {upload.isPending ? (
            <Spinner size={22} />
          ) : (
            <Icon name="upload" size={24} className="text-muted" />
          )}
          <div className="text-xs text-muted">
            {upload.isPending ? "Lädt hoch…" : dropLabel}
          </div>
          <div className="text-[10px] text-muted">{hint}</div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={max > 1}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {error && <p className="text-[11px] text-danger">{error}</p>}
    </div>
  );
}

// D65: `result` = die Ergebnis-Assets des geöffneten Jobs (sonst leer); `resultUrls` = ihre
// presigned-GET-`downloadUrl`s je Asset-ID (D56) für die echte Vorschau im Renderer.
// D68: `signals` = die persistierten Job-Signale (pack.meta/loopScore) für den Player.
export function MediaPanel({
  core,
  ui,
  result = [],
  resultUrls = {},
  signals = {},
  jobStatus = null,
}: {
  core: ToolCore;
  ui: WebToolUI | undefined;
  result?: AssetRef[];
  resultUrls?: Record<string, string>;
  signals?: JobSignals;
  // D76 — Status des geöffneten Jobs (für den „Trotzdem generieren"-Pfad), via Context an den Renderer.
  jobStatus?: string | null;
}) {
  const Renderer = ui?.ResultRenderer ?? FallbackRenderer;
  // Eingabe-Spalte, sobald das Tool überhaupt Eingaben AUFNEHMEN kann (max>0) — auch rein
  // optionale Referenzbilder (image-generator `reference` min:0/max:2: seit D76 echte Qwen-Edit-
  // Refs, sonst gäbe es keine Upload-Dropzone und der User könnte keine Refs anhängen). Nur Tools
  // ganz OHNE Input-Slot (max==0) → Output über die volle Breite (§11). PFLICHT-Eingaben (min>0,
  // chroma-key/spritesheet) erzwingt separat `inputsSatisfied` im Submit-Gate.
  // D91: rein deklarative Slot-Sichtbarkeit (`visibleWhen`) gegen den aktuellen Settings-Draft —
  // ein Slot ohne `visibleWhen` ist immer sichtbar; hält die Shell generisch (kein Tool-Hardcoding,
  // §1/§4). Bsp.: `end-frame` erscheint nur bei `loop===false`.
  const draft = useUiStore((s) => s.drafts[core.id]);
  const visibleInputs = core.inputs.filter(
    (req) => !req.visibleWhen || draft?.[req.visibleWhen.setting] === req.visibleWhen.equals,
  );
  const hasInputs = visibleInputs.some((req) => req.max > 0);
  return (
    // Natürlicher Fluss (kein flex-1/min-h-0-Klemmen): Höhe folgt dem Inhalt, Scrollen besorgt der
    // äußere Studio-Container. So wächst der Output-Bereich mit dem Ergebnis statt es intern zu klemmen.
    <div className="flex flex-col gap-3">
      {/* §11: Input LINKS, Output/Player RECHTS (nebeneinander statt gestapelt). Nur Tools GANZ
          ohne Input-Slot (max=0) → Output über die volle Breite; optionale Refs (image-generator)
          bekommen die Spalte. Unter 1280px (xl) fällt es einspaltig zurück (Input über Output). */}
      <div
        className={cx(
          "grid items-start gap-3",
          hasInputs ? "grid-cols-1 xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)]" : "grid-cols-1",
        )}
      >
        {/* Input-Dropzones (je Rolle, untereinander in der schmalen linken Spalte) */}
        {hasInputs && (
          <div className="flex min-w-0 flex-col gap-3">
            {visibleInputs.map((req) => (
              <Dropzone
                key={req.role}
                toolId={core.id}
                role={req.role}
                kind={req.kind}
                kinds={req.kinds ?? [req.kind]}
                min={req.min}
                max={req.max}
              />
            ))}
          </div>
        )}

        {/* Output / Player — presigned URLs (D65) + Job-Signale (D68) fließen über Context in den
            (Tool-)Renderer; die ResultRendererProps-Signatur (assets) bleibt unverändert. */}
        <div className="flex min-w-0 flex-col gap-2">
          {/* Spacer in Höhe der Input-Header-Zeile (text-[11px] + gap-2), damit die Card oben bündig
              mit dem Input-Quadrat startet statt mit dessen Rollen-Titel. */}
          {hasInputs && (
            <div className="text-[11px]" aria-hidden>
              &nbsp;
            </div>
          )}
          {/* min-h = Input-Image-Höhe (max-w-[200px] aspect-square), damit die leere Card oben UND
              unten bündig mit dem Input-Quadrat abschließt (Leer-Box-min-h in FallbackRenderer ist
              auf das Panel-Padding/Border abgestimmt). Mit Ergebnis wächst die Card frei. */}
          <div className="panel min-h-[200px] min-w-0 p-4">
            <ResultUrlProvider value={resultUrls}>
              <ResultMetaProvider value={signals}>
                {/* D76 — Job-Status (z.B. "failed") als eigener Context neben URLs/Signalen. */}
                <JobStatusProvider value={jobStatus}>
                  <Renderer assets={result} />
                </JobStatusProvider>
              </ResultMetaProvider>
            </ResultUrlProvider>
          </div>
        </div>
      </div>
    </div>
  );
}
